import React, { useState } from 'react';
import { 
  BellIcon,
  XMarkIcon,
  CheckIcon,
  TrashIcon,
  Cog6ToothIcon,
  EyeIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { useNotifications } from '../../contexts/NotificationContext';
import { NotificationType, NotificationPriority } from '../../types/notification';
import { useNavigate } from 'react-router-dom';

interface NotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ isOpen, onClose }) => {
  const { 
    notifications, 
    unreadCount, 
    markAsRead, 
    markAllAsRead, 
    deleteNotification, 
    clearAllNotifications 
  } = useNotifications();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'all' | 'unread'>('all');

  const filteredNotifications = filter === 'unread' 
    ? notifications.filter(n => !n.isRead)
    : notifications;

  const getNotificationIcon = (type: NotificationType, priority: NotificationPriority) => {
    const iconClass = `w-5 h-5 ${getIconColor(type, priority)}`;
    
    switch (type) {
      case NotificationType.BOOKING:
        return <CheckCircleIcon className={iconClass} />;
      case NotificationType.PAYMENT:
        return <InformationCircleIcon className={iconClass} />;
      case NotificationType.FLEET:
        return <ExclamationTriangleIcon className={iconClass} />;
      case NotificationType.MAINTENANCE:
        return <Cog6ToothIcon className={iconClass} />;
      case NotificationType.SECURITY:
        return <XCircleIcon className={iconClass} />;
      case NotificationType.SYSTEM:
        return <InformationCircleIcon className={iconClass} />;
      case NotificationType.BLOG:
        return <CheckIcon className={iconClass} />;
      case NotificationType.USER:
        return <InformationCircleIcon className={iconClass} />;
      default:
        return <BellIcon className={iconClass} />;
    }
  };

  const getIconColor = (type: NotificationType, priority: NotificationPriority) => {
    if (priority === NotificationPriority.URGENT) return 'text-red-500';
    if (priority === NotificationPriority.HIGH) return 'text-orange-500';
    
    switch (type) {
      case NotificationType.BOOKING:
        return 'text-green-500';
      case NotificationType.PAYMENT:
        return 'text-blue-500';
      case NotificationType.FLEET:
        return 'text-purple-500';
      case NotificationType.MAINTENANCE:
        return 'text-yellow-500';
      case NotificationType.SECURITY:
        return 'text-red-500';
      case NotificationType.SYSTEM:
        return 'text-gray-500';
      case NotificationType.BLOG:
        return 'text-indigo-500';
      case NotificationType.USER:
        return 'text-teal-500';
      default:
        return 'text-gray-500';
    }
  };

  const getPriorityBadge = (priority: NotificationPriority) => {
    const baseClass = 'px-2 py-1 text-xs font-medium rounded-full';
    
    switch (priority) {
      case NotificationPriority.URGENT:
        return `${baseClass} bg-red-100 text-red-800`;
      case NotificationPriority.HIGH:
        return `${baseClass} bg-orange-100 text-orange-800`;
      case NotificationPriority.MEDIUM:
        return `${baseClass} bg-yellow-100 text-yellow-800`;
      case NotificationPriority.LOW:
        return `${baseClass} bg-gray-100 text-gray-800`;
      default:
        return `${baseClass} bg-gray-100 text-gray-800`;
    }
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    
    return date.toLocaleDateString();
  };

  const handleNotificationClick = (notification: any) => {
    if (!notification.isRead) {
      markAsRead(notification.id);
    }
    
    if (notification.actionUrl) {
      // Convert deep links like /bookings/ID to /bookings?highlight=ID
      // since we don't have individual detail routes
      const url = notification.actionUrl;
      
      if (url.startsWith('/bookings/')) {
        const bookingId = url.replace('/bookings/', '');
        navigate(`/bookings?highlight=${bookingId}`);
      } else if (url.startsWith('/fleet/')) {
        const vehicleId = url.replace('/fleet/', '');
        navigate(`/fleet?highlight=${vehicleId}`);
      } else {
        navigate(url);
      }
      onClose();
    } else if (notification.type === 'booking') {
      navigate('/bookings');
      onClose();
    } else if (notification.type === 'fleet' || notification.type === 'maintenance') {
      navigate('/fleet');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40" onClick={onClose} />
      
      {/* Dropdown */}
      <div className="absolute right-0 mt-2 w-96 bg-white rounded-2xl shadow-2xl border border-gray-200 z-50 max-h-[600px] overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <BellIcon className="w-5 h-5 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">Notifications</h3>
              {unreadCount > 0 && (
                <span className="px-2 py-1 text-xs font-bold bg-red-500 text-white rounded-full">
                  {unreadCount}
                </span>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          
          {/* Filter and Actions */}
          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                  filter === 'all' 
                    ? 'bg-blue-100 text-blue-700 font-medium' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                All ({notifications.length})
              </button>
              <button
                onClick={() => setFilter('unread')}
                className={`px-3 py-1 text-sm rounded-lg transition-colors ${
                  filter === 'unread' 
                    ? 'bg-blue-100 text-blue-700 font-medium' 
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Unread ({unreadCount})
              </button>
            </div>
            
            <div className="flex gap-1">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
                  title="Mark all as read"
                >
                  <EyeIcon className="w-4 h-4 text-gray-500" />
                </button>
              )}
              <button
                onClick={clearAllNotifications}
                className="p-1 hover:bg-gray-200 rounded-lg transition-colors"
                title="Clear all notifications"
              >
                <TrashIcon className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>
        </div>

        {/* Notifications List */}
        <div className="max-h-96 overflow-y-auto">
          {filteredNotifications.length === 0 ? (
            <div className="p-8 text-center">
              <BellIcon className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">
                {filter === 'unread' ? 'No unread notifications' : 'No notifications'}
              </p>
              <p className="text-sm text-gray-400 mt-1">
                {filter === 'unread' 
                  ? 'All caught up! Check back later for updates.' 
                  : 'Notifications will appear here when you receive them.'
                }
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 hover:bg-gray-50 transition-colors cursor-pointer ${
                    !notification.isRead ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 mt-1">
                      {getNotificationIcon(notification.type, notification.priority)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <p className={`text-sm font-medium ${
                            !notification.isRead ? 'text-gray-900' : 'text-gray-700'
                          }`}>
                            {notification.title}
                          </p>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {notification.message}
                          </p>
                        </div>
                        
                        <div className="flex flex-col items-end gap-1">
                          <span className={getPriorityBadge(notification.priority)}>
                            {notification.priority}
                          </span>
                          {!notification.isRead && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-xs text-gray-500">
                          {formatTimeAgo(notification.createdAt)}
                        </span>
                        
                        <div className="flex gap-1">
                          {!notification.isRead && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification.id);
                              }}
                              className="p-1 hover:bg-gray-200 rounded transition-colors"
                              title="Mark as read"
                            >
                              <CheckIcon className="w-3 h-3 text-gray-500" />
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteNotification(notification.id);
                            }}
                            className="p-1 hover:bg-gray-200 rounded transition-colors"
                            title="Delete notification"
                          >
                            <XMarkIcon className="w-3 h-3 text-gray-500" />
                          </button>
                        </div>
                      </div>
                      
                      {notification.actionLabel && (
                        <div className="mt-2">
                          <span className="text-xs text-blue-600 font-medium">
                            {notification.actionLabel} →
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {notifications.length > 0 && (
          <div className="p-3 border-t border-gray-200 bg-gray-50">
            <button
              onClick={() => {
                navigate('/notifications');
                onClose();
              }}
              className="w-full text-center text-sm text-blue-600 hover:text-blue-700 font-medium py-2 hover:bg-blue-50 rounded-lg transition-colors"
            >
              View All Notifications
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default NotificationDropdown;